// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';

import {
  CellRendererFunc,
  ColumnGroup,
  CommonDataTableFields,
  GroupSortableColumnsDictionary,
  SegmentColumnSpec,
  TableColumn,
  TableColumnType,
  TableDataType,
} from 'common/components/data-table/types';
import {PropertyType} from 'common/report/constants';
import {OVERALL_PROP, TableToggleableMetrics} from 'common/report/funnels/models/constants';
import {
  BasicFunnelStep,
  FunnelSegment,
  FunnelStep,
  PValue,
  SelectedColumns,
  TableNonToggleableMetrics,
} from 'common/report/funnels/models/types';
import {getTableColumnSpec} from 'common/report/insights/util/table-width';
import {formatDuration, MS_BY_UNIT} from 'common/util/date';
import {LiftType} from 'common/report/util/lift/types';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {DEFAULT_LIFT_COLUMN_WIDTH, GROUP_HEADER_MIN_WIDTH} from 'common/components/data-table/constants';
import {renamePropertyValue} from 'common/query-builder/cohorts-query-builder/util';
import {ShowClause} from 'common/report/insights/util/types';
import {formatMetricNumberWithUnits} from 'common/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {isFunnelConversionShowClause} from 'common/report/insights/models/utils';
import {MetricsResource} from 'common/report/model-resources/metrics';

import {getFinalDisplayOptions} from '../util';
import {getCellStatSigRenderer, liftCellRenderer, StatSigClickHandler} from './cell-renderers';
import {FunnelTableRow, StatSigDisplay, StatSigDisplayStatusColor} from './types';

function overallConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.overall_conv_ratio * 100, 2);
}

function stepConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.step_conv_ratio * 100, 2);
}

function formatStepConversionDuration(seconds) {
  if (!isNumber(seconds)) {
    return `-`;
  }
  const duration =
    seconds < 500 ? {value: round(seconds, 0), unit: `s`} : formatDuration(seconds * MS_BY_UNIT.second, 1, true);
  return `${duration.value}${duration.unit}`;
}

function getStatisticalSignificance(pValue: PValue): StatSigDisplay {
  const {confidence, is_interesting: isInteresting, insufficient_samples: insufficientSamples} = pValue;
  if (insufficientSamples) {
    return {confidence: `-`};
  }
  return {
    confidence: confidence > 0.99 ? `0.99` : confidence.toFixed(2),
    status: isInteresting ? StatSigDisplayStatusColor.Green : StatSigDisplayStatusColor.Red,
    label: isInteresting ? `Statistically significant` : `Not statistically significant`,
  };
}

function stepStatsLabelTransform(row: FunnelTableRow, field: string): string {
  return formatStepConversionDuration(row[field]);
}

function statSigSortTransform(row: FunnelTableRow): number {
  if (!row.statSig) {
    return null;
  }
  const {status, confidence} = row.statSig;
  const {count} = row.displayInfo.steps[0];
  const totalFunnelSteps = row.displayInfo.steps;
  const {conversionRatio} = row.displayInfo.steps[totalFunnelSteps.length - 1];
  if (confidence === ``) {
    return Number.NEGATIVE_INFINITY;
  } else if (status === StatSigDisplayStatusColor.Green) {
    return -1 * conversionRatio;
  } else if (status === StatSigDisplayStatusColor.Red) {
    return 1.0 / count;
  } else {
    return 1 + 1.0 / count;
  }
}

export const FIELD_STAT_SIG = `statSig`;
export const DEFAULT_SORT_ORDER = 1;
export const DEFAULT_SORT_FIELD = FIELD_STAT_SIG;

const DATA_TABLE_LIFT_PREFIX = `lift-`;
const DATA_TABLE_LIFT_EXTRA_PREFIX = `liftExtra-`;

function getDataTableColumnIdentifiers(
  step,
  index: number,
  liftType?: LiftType,
  liftStep?: FunnelStep,
  baselineStep?: FunnelStep,
) {
  const segmentDict = {};

  const stepCountKey = `step-${index}`;
  const stepPct = `step-${index}-pct`;
  const stepOverallPct = `step-${index}-overall-pct`;
  const statisticsOverall = `statistics-overall-${index}`;
  const statisticsAvg = `statistics-${index}`;

  segmentDict[stepCountKey] = step.count;
  segmentDict[stepPct] = step.step_conv_ratio;
  segmentDict[stepOverallPct] = step.overall_conv_ratio;
  segmentDict[statisticsOverall] = step.avg_time_from_start;
  segmentDict[statisticsAvg] = step.avg_time;

  if (liftType) {
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepCountKey] = liftStep.count;
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepPct] = liftStep.step_conv_ratio;
    segmentDict[DATA_TABLE_LIFT_PREFIX + stepOverallPct] = liftStep.overall_conv_ratio;
    segmentDict[DATA_TABLE_LIFT_PREFIX + statisticsOverall] = liftStep.avg_time_from_start;
    segmentDict[DATA_TABLE_LIFT_PREFIX + statisticsAvg] = liftStep.avg_time;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepCountKey] = baselineStep?.count || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepPct] = baselineStep?.step_conv_ratio || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + stepOverallPct] = baselineStep?.overall_conv_ratio || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + statisticsOverall] = baselineStep?.avg_time_from_start || 0;
    segmentDict[DATA_TABLE_LIFT_EXTRA_PREFIX + statisticsAvg] = baselineStep?.avg_time || 0;
  }

  return segmentDict;
}

/** Columns in step and step metric chart data tables that can be group sorted.
 * Key is the field that is group sortable, value is column fields to add (at least sortSelectTitle) */
export const GroupSortableStepColumns: GroupSortableColumnsDictionary = {
  'step-0': {sortSelectTitle: `Top of Funnel`, tooltip: `Number who performed this step\n(Top of Funnel)`},
  overall: {sortSelectTitle: `Total Conv.`},
  [FIELD_STAT_SIG]: {sortSelectTitle: `Sig.`},
};

export function getColumnGroups({
  liftType,
  metricsResource,
  onStatSigClick,
  showClause,
  segmentSpec,
  segmentTypes,
  selectedColumns,
  shouldShowStatSig,
  showStepNamesInGroupHeaders,
  stepNames,
  tableWidth,
}: {
  liftType?: LiftType;
  metricsResource: MetricsResource;
  onStatSigClick: StatSigClickHandler;
  showClause: ShowClause;
  segmentSpec: SegmentColumnSpec[];
  segmentTypes: PropertyType[];
  selectedColumns: SelectedColumns;
  shouldShowStatSig: boolean;
  showStepNamesInGroupHeaders: boolean;
  stepNames: string[];
  tableWidth?: number;
}): ColumnGroup[] {
  const renamedSegments = segmentSpec.map(({title}) => title);
  const isSegmented = Boolean(renamedSegments.length);

  // TODO: Remove width computation logic after resizing layout is shipped
  const spec = getTableColumnSpec(
    selectedColumns,
    renamedSegments.length,
    stepNames.length,
    shouldShowStatSig,
    tableWidth,
  );

  // We only want to format the value if it matches the math type of the show
  // clause. For example if the math type is "Conversion Rate" we only want to
  // format the percent value (the actual conversion rate) and not the absolute
  // value and vice versa if our math type is Unique Users or something else.
  const isPercentageView = isFunnelConversionShowClause(showClause);

  const groups: TableColumn[][] = spec.map((columnGroup): TableColumn[] =>
    columnGroup.map((column, idx): TableColumn => {
      // Handle property/metric units
      const labelTransform = (row, field) => {
        switch (column.identifier) {
          case TableToggleableMetrics.Count: {
            const options = !isPercentageView
              ? getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metricsResource, {})
              : {};
            return formatMetricNumberWithUnits(row[field], options);
          }
          case TableToggleableMetrics.ConvFirstStep:
          case TableToggleableMetrics.ConvPrevStep:
          case TableNonToggleableMetrics.OverallConv: {
            const defaultOptions = {suffix: `%`};
            const options = isPercentageView
              ? getFinalDisplayOptions(showClause, eventPropertyDefinitionsStore, metricsResource, defaultOptions)
              : defaultOptions;
            return formatMetricNumberWithUnits(row[field] * 100, options);
          }
          case TableToggleableMetrics.TimeFirstStep:
          case TableToggleableMetrics.TimePrevStep:
            return stepStatsLabelTransform(row, field);

          case TableNonToggleableMetrics.Segment:
            return renamePropertyValue(row[field], renamedSegments?.[column.segmentIdx]);

          case TableToggleableMetrics.StatSig:
          case TableNonToggleableMetrics.NoMetrics:
            return row[field];

          default:
            console.error(
              `Unrecognized column identifier for label transform: ${column.identifier} is not handled. Defaulting to unformatted label.`,
            );
            return row[field];
        }
      };

      let cellRenderer: CellRendererFunc<FunnelTableRow>;
      if (liftType) {
        cellRenderer = liftCellRenderer(liftType, labelTransform);
      }

      let initialWidth: number;
      if (liftType && column.identifier !== TableToggleableMetrics.StatSig) {
        initialWidth = DEFAULT_LIFT_COLUMN_WIDTH;
      }

      const commonProps = {
        isSortable: true,
        fixedWidth: !showStepNamesInGroupHeaders,
        last: idx === columnGroup.length - 1,
        labelTransform,
        ...(Number.isFinite(initialWidth) ? {initialWidth} : {}),
        ...(cellRenderer ? {cellRenderer} : {}),
      };

      switch (column.identifier) {
        case TableNonToggleableMetrics.Segment:
          if (isSegmented) {
            const field = `${CommonDataTableFields.SegmentPrefix}${column.segmentIdx}`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Segment,
              contentType: segmentTypes[column.segmentIdx],
              title: renamedSegments[column.segmentIdx],
              sticky: true,
            };
          } else {
            const field = `${CommonDataTableFields.SegmentPrefix}0`;
            return {
              ...commonProps,
              field,
              liftField: DATA_TABLE_LIFT_PREFIX + field,
              liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
              type: TableColumnType.Segment,
              contentType: segmentTypes[column.segmentIdx],
              title: `Property`,
              sticky: true,
            };
          }
        case TableNonToggleableMetrics.OverallConv: {
          const field = `overall`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            resizeMinWidth: 75,
            sticky: true,
            title: `Total Conv.`,
            tooltip: ({t}) => t(`funnels.table.total-tooltip`),
            ...GroupSortableStepColumns[`overall`],
          };
        }

        case TableToggleableMetrics.StatSig:
          return {
            ...commonProps,
            field: FIELD_STAT_SIG,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            cellRenderer: getCellStatSigRenderer(onStatSigClick),
            sortTransform: statSigSortTransform,
            sticky: true,
            title: `Sig.`,
            tooltip: ({t}) => t(`funnels.table.stat-sig-tooltip`),
            ...GroupSortableStepColumns[FIELD_STAT_SIG],
          };
        case TableToggleableMetrics.Count: {
          const field = `step-${column.stepIdx}`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.step-user-count-tooltip`),
            icon: `value-absolute`,
            ...(field in GroupSortableStepColumns ? GroupSortableStepColumns[field] : {}),
            ...(column.stepIdx === 0 ? {initialWidth: GROUP_HEADER_MIN_WIDTH} : {}),
          };
        }
        case TableToggleableMetrics.ConvPrevStep: {
          const field = `step-${column.stepIdx}-pct`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.prev-step-tooltip`),
            icon: `conversion-prev-step`,
          };
        }
        case TableToggleableMetrics.ConvFirstStep: {
          const field = `step-${column.stepIdx}-overall-pct`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.first-step-tooltip`),
            icon: `conversion-first-step`,
          };
        }
        case TableToggleableMetrics.TimePrevStep: {
          const field = `statistics-${column.stepIdx}`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.prev-step-avg-time-tooltip`),
            icon: `time-prev-step`,
          };
        }
        case TableToggleableMetrics.TimeFirstStep: {
          const field = `statistics-overall-${column.stepIdx}`;
          return {
            ...commonProps,
            field,
            liftField: DATA_TABLE_LIFT_PREFIX + field,
            liftExtra: DATA_TABLE_LIFT_EXTRA_PREFIX + field,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.first-step-avg-time-tooltip`),
            icon: `time-first-step`,
          };
        }
        case TableNonToggleableMetrics.NoMetrics:
        default:
          return {
            ...commonProps,
            field: `unselected`,
            type: TableColumnType.Data,
            contentType: TableDataType.Number,
            tooltip: ({t}) => t(`funnels.table.add-metrics-tooltip`),
            title: `No metrics`,
            isSortable: false,
          };
      }
    }),
  );

  if (showStepNamesInGroupHeaders) {
    const groupsWithHeaders = groups
      .slice(-stepNames.length)
      .map((columns, idx) => ({columns, title: stepNames[idx], funnelStepNumber: idx + 1}));
    const stickyGroups = groups.slice(0, groups.length - groupsWithHeaders.length).map((columns) => ({columns}));

    return [...stickyGroups, ...groupsWithHeaders];
  }

  return groups.map((columns) => ({columns}));
}

function shouldShowPValues(segmentSteps: FunnelSegment[]): boolean {
  return !!segmentSteps && segmentSteps.length > 0;
}

function getFunnelLiftSegment({
  segmentIdx,
  stepIndex,
  liftComparison,
  liftSegmentStepsMap,
  compareSegmentStepsMap,
  segmentSteps,
}: {
  segmentIdx: number;
  stepIndex: number;
  liftComparison: LiftComparison;
  segmentSteps: FunnelSegment[];
  liftSegmentStepsMap: Record<string, FunnelSegment>;
  compareSegmentStepsMap: Record<string, FunnelSegment>;
}): {
  liftStep: FunnelStep;
  baselineStep?: FunnelStep;
} {
  const baseSegment = segmentSteps[segmentIdx];
  switch (liftComparison.type) {
    case LiftType.Overall:
      // Not supported for steps chart
      return null;
    case LiftType.Segment: {
      const segmentProps = baseSegment.props.map((prop) => prop.prop);
      const baselineSegment = getBaselineSegment({
        funnelSegments: segmentSteps,
        segmentProps,
        groupValue: liftComparison.groupValue,
      });
      const liftSegment = liftSegmentStepsMap[baseSegment.propName];
      return {
        liftStep: liftSegment.steps[stepIndex],
        baselineStep: baselineSegment ? baselineSegment.steps[stepIndex] : null,
      };
    }
    case LiftType.Time: {
      const liftSegment = liftSegmentStepsMap[baseSegment.propName];
      const compareSegment = compareSegmentStepsMap[baseSegment.propName];
      return {
        liftStep: liftSegment.steps[stepIndex],
        baselineStep: compareSegment ? compareSegment.steps[stepIndex] : null,
      };
    }
    default:
      return null;
  }
}

function getSegmentStepsMap({
  compareSegmentSteps = null,
  liftSegmentSteps = null,
}: {
  compareSegmentSteps?: FunnelSegment[] | null;
  liftSegmentSteps?: FunnelSegment[] | null;
}): {
  compareSegmentStepsMap: Record<string, FunnelSegment> | null;
  liftSegmentStepsMap: Record<string, FunnelSegment> | null;
} {
  let compareSegmentStepsMap: Record<string, FunnelSegment> | null = null;
  if (compareSegmentSteps) {
    compareSegmentStepsMap = compareSegmentSteps.reduce((acc, segment) => {
      acc[segment.propName] = segment;
      return acc;
    }, {});
  }

  let liftSegmentStepsMap: Record<string, FunnelSegment> | null = null;
  if (liftSegmentSteps) {
    liftSegmentStepsMap = liftSegmentSteps.reduce((acc, segment) => {
      acc[segment.propName] = segment;
      return acc;
    }, {});
  }

  return {compareSegmentStepsMap, liftSegmentStepsMap};
}

export function getBaselineSegment({
  funnelSegments,
  segmentProps,
  groupValue,
}: {
  funnelSegments: FunnelSegment[];
  segmentProps: string[];
  groupValue: string;
}) {
  return funnelSegments.find((segment) => {
    const props = segment.props;
    for (let i = 0; i < props.length - 1; i++) {
      if (props[i].prop !== segmentProps[i]) {
        return false;
      }
    }
    return props[props.length - 1].prop === groupValue;
  });
}

// TODO opslead
export function getRows({
  selected,
  controlGroup,
  segmentSteps,
  shouldShowStatSig = true,
  shouldFilterForSelected = false,
  liftComparison,
  liftSegmentSteps = null,
  compareSegmentSteps = null,
}: {
  selected: Map<string, string>;
  segmentSteps: FunnelSegment[];
  controlGroup: string[];
  shouldShowStatSig?: boolean;
  shouldFilterForSelected?: boolean;
  liftComparison?: LiftComparison;
  liftSegmentSteps?: FunnelSegment[] | null;
  compareSegmentSteps?: FunnelSegment[] | null;
}): FunnelTableRow[] {
  if (!segmentSteps.length) {
    return [];
  }
  const displayPValues = shouldShowStatSig && shouldShowPValues(segmentSteps);

  const totalFunnelSteps = segmentSteps[0].steps.length;
  const {compareSegmentStepsMap, liftSegmentStepsMap} = getSegmentStepsMap({compareSegmentSteps, liftSegmentSteps});
  const segmentRows = segmentSteps.flatMap((segment, segmentIdx) => {
    if (shouldFilterForSelected && !selected.has(segment.prop)) {
      return [];
    }
    const averageTimeBetweenSteps = [];
    const infoSteps = [];
    const isOverall = segment.prop === OVERALL_PROP;
    segment.steps.forEach((step) => {
      averageTimeBetweenSteps.push(step.avg_time);
      infoSteps.push({
        count: step.count,
        conversionRatio: isOverall ? overallConversionRatio(step) : stepConversionRatio(step),
      });
    });

    const prop = segment.prop;

    const isControlGroup = controlGroup?.length
      ? isEqual(
          controlGroup,
          segment.props.map((p) => p.prop),
        )
      : isOverall;

    let liftOverallSegments = {};
    if (liftComparison) {
      const liftSegmentForOverall = getFunnelLiftSegment({
        segmentIdx,
        stepIndex: segment.steps.length - 1,
        liftComparison,
        segmentSteps,
        liftSegmentStepsMap,
        compareSegmentStepsMap,
      });
      liftOverallSegments = {
        [DATA_TABLE_LIFT_PREFIX + `overall`]: liftSegmentForOverall.liftStep.overall_conv_ratio,
        [DATA_TABLE_LIFT_EXTRA_PREFIX + `overall`]: liftSegmentForOverall.baselineStep?.overall_conv_ratio || 0,
      };
    }

    const segmentDict: FunnelTableRow = {
      // TODO: Duplicate of prop in FunnelTableRow. Dedupe
      identifier: prop,
      prop,
      props: segment.props,
      overall: segment.steps[segment.steps.length - 1].overall_conv_ratio,
      ...liftOverallSegments,
      isSelected: selected.has(segment.prop),
      isControlGroup,
      displayInfo: {
        steps: infoSteps,
        averageTimeBetweenSteps,
      },
    };

    if (segmentDict.isSelected) {
      segmentDict.checkedColor = selected.get(segment.prop);
    }

    segment.props.forEach((segmentId, index) => {
      segmentDict[`${CommonDataTableFields.SegmentPrefix}${index}`] = segmentId.propName ?? segmentId.prop;
    });

    segment.steps.forEach((step, stepIndex) => {
      const liftData = liftComparison
        ? getFunnelLiftSegment({
            segmentIdx,
            stepIndex,
            liftComparison,
            segmentSteps,
            liftSegmentStepsMap,
            compareSegmentStepsMap,
          })
        : null;
      const segmentDataKeys = getDataTableColumnIdentifiers(
        step,
        stepIndex,
        liftComparison?.type,
        liftData?.liftStep,
        liftData?.baselineStep,
      );
      Object.assign(segmentDict, segmentDataKeys);
    });

    if (isOverall) {
      segmentDict.statSig = {confidence: ``};
    } else if (displayPValues && !isControlGroup) {
      const lastStep = segment.steps[totalFunnelSteps - 1];

      if (hasPValue(lastStep)) {
        segmentDict.statSig = getStatisticalSignificance(lastStep.p_value);
      }
    }
    return segmentDict;
  });

  // Move Overall row to first position
  const overallIndex = segmentRows.findIndex((row) => row.prop === OVERALL_PROP);
  if (overallIndex !== -1) {
    const overallRow = segmentRows.splice(overallIndex, 1)[0];
    segmentRows.splice(0, 0, overallRow);
  }

  // Move control group row to be the first position after Overall
  const controlGroupIndex = segmentRows.findIndex((row) => row.isControlGroup);
  const hasOverall = overallIndex !== -1;
  if (controlGroupIndex !== -1 && controlGroupIndex !== 0) {
    const controlGroupRow = segmentRows.splice(controlGroupIndex, 1)[0];
    segmentRows.splice(hasOverall ? 1 : 0, 0, controlGroupRow);
  }

  return segmentRows;
}

function hasPValue(step: BasicFunnelStep | FunnelStep): step is Required<Pick<FunnelStep, 'p_value'>> & FunnelStep {
  return 'p_value' in step;
}
